import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { shippingService } from "../services/dataService";
import { ShipmentDetail, ShipmentListItem, ShipmentStatus as S } from "../types";
import { usePermissions } from "../context/PermissionContext";
import { EPS, SH_STATUS_CLASS, SH_STATUS_LABEL, ShAge, shAge, shErr, shFmtDate, shFmtDateTime, shFmtQty, shLayerBlocked } from "../utils/shipping";
import { firmHue, firmInitials } from "./FirmPicker";
import "../pages/PurchaseOrders.css";
import "../pages/Shipping.css";

/**
 * Sevkiyat kartı (çekmece) — salt okunur belge görünümü. İşlemleri kendisi yapmaz;
 * sayfadan ister (düzenle / sevk et / teslim / iptal / sil / yazdır), böylece onay ve bildirimler tek yerde durur.
 */
type HeadInfo = Pick<ShipmentListItem, "number" | "firmName" | "firmCode" | "status" | "shipmentDate" | "shippedAt" | "waybillNo">;

export interface ShipmentDrawerProps {
  shipmentId: string;
  /** Liste satırı: ayrıntı yüklenirken başlık anında dolar. */
  preview?: ShipmentListItem;
  /** Sayfa her değişiklikten sonra artırır → ayrıntı yeniden çekilir. */
  refreshKey: number;
  busy?: boolean;
  escDisabled?: boolean;
  onClose: () => void;
  onEdit: (d: ShipmentDetail) => void;
  onShip: (d: ShipmentDetail) => void;
  onDeliver: (d: ShipmentDetail) => void;
  onCancel: (d: ShipmentDetail) => void;
  onDelete: (o: { shipmentId: string; number: string }) => void;
  onPrint: (o: { shipmentId: string; number: string }) => void;
}

const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" /></svg>,
  receive: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M7 10l5 5 5-5M4 19h16" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>,
  print: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><path d="M6 14h12v7H6z" /></svg>,
};

const NO_AGE: ShAge = { days: null, state: "", stuck: false, text: "" };

const ShipmentDrawer: React.FC<ShipmentDrawerProps> = ({ shipmentId, preview, refreshKey, busy, escDisabled, onClose, onEdit, onShip, onDeliver, onCancel, onDelete, onPrint }) => {
  const navigate = useNavigate();
  const { has, hasModule } = usePermissions();
  const [shipment, setShipment] = useState<ShipmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const overlayRef = useRef<HTMLDivElement>(null);
  const asideRef = useRef<HTMLElement>(null);
  const gen = useRef(0);

  const load = useCallback(async () => {
    const g = ++gen.current;
    setLoading(true); setError("");
    try {
      const d = await shippingService.shipment(shipmentId);
      if (g === gen.current) setShipment(d);
    } catch (e: any) {
      if (g === gen.current) { setShipment(null); setError(e?.response?.status === 404 ? "" : shErr(e, "")); }
    } finally { if (g === gen.current) setLoading(false); }
  }, [shipmentId]);
  // Başka bir sevkiyata geçilince eski kart gösterilmesin; aynı sevkiyatta yenilemede eski içerik kalır
  useEffect(() => { setShipment((s) => (s && s.shipmentId === shipmentId ? s : null)); }, [shipmentId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  // aria-modal kart odağı alır (derin bağlantıda da); kapanınca odak açıldığı yere döner
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    return () => { if (prev && prev.isConnected) prev.focus?.({ preventScroll: true }); };
  }, []);
  useEffect(() => {
    const el = asideRef.current;
    if (el && !el.contains(document.activeElement)) el.focus({ preventScroll: true });
  }, [shipmentId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented && !escDisabled && !shLayerBlocked(overlayRef.current)) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, escDisabled]);

  const h: HeadInfo | null = shipment ?? preview ?? null;
  const age = h ? shAge(h.shippedAt, h.status) : NO_AGE;
  const canUpdate = has("shipping:update");
  const canDelete = has("shipping:delete");
  const stockOn = hasModule("stock");

  const lines = shipment?.lines ?? [];
  // Birim bazında toplam: "1.250 Adet · 40 Metre"
  const unitTotals = Array.from(lines.reduce((m, l) => m.set(l.unit, (m.get(l.unit) ?? 0) + l.quantity), new Map<string, number>()), ([u, q]) => `${shFmtQty(q)} ${u}`).join(" · ");
  const linePk = lines.reduce((a, l) => a + (l.packageCount ?? 0), 0);
  const pk = shipment?.packageCount ?? (linePk || null);
  const anyStock = lines.some((l) => l.hasStockMovement);
  const showStock = !!shipment && shipment.status !== S.Draft && (stockOn || anyStock);

  return (
    <div className="po-overlay" ref={overlayRef} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="po-drawer" ref={asideRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="po-dr-title">
        <div className="po-drawer-head">
          <div className="po-drawer-title">
            {h && <span className="po-mark big" style={{ ["--po-hue" as any]: firmHue(h.firmName) }}>{firmInitials(h.firmName)}</span>}
            <div>
              <span className="po-kicker">Sevkiyat{h ? ` · ${shFmtDate(h.shipmentDate)}` : ""}</span>
              <h2 id="po-dr-title">{h?.number ?? "Sevkiyat"}</h2>
              {h && <div className="po-drawer-sup">{h.firmName}{h.firmCode && <span className="po-codepill">{h.firmCode}</span>}</div>}
              {h && (
                <div className="po-badges">
                  <span className={`po-st ${SH_STATUS_CLASS[h.status]}`}>{SH_STATUS_LABEL[h.status]}</span>
                  {age.stuck && <span className={`po-badge ${age.state === "late" ? "err" : "warn"}`}>{age.text}</span>}
                  {h.waybillNo && <span className="po-badge muted">İrsaliye {h.waybillNo}</span>}
                  {!stockOn && h.status === S.Draft && <span className="po-badge muted">stok hareketi üretilmez</span>}
                </div>
              )}
            </div>
          </div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose}>{I.close}</button>
        </div>

        <div className="po-drawer-body">
          {!shipment && loading && <div className="po-loading"><span className="po-spin" /> Sevkiyat yükleniyor</div>}

          {!shipment && !loading && (
            <div className="po-blank">
              <span className="po-blank-icon">{I.inbox}</span>
              <b>Sevkiyat bulunamadı</b>
              {error || "Silinmiş ya da erişiminiz yok."}
              <div className="po-actrow" style={{ justifyContent: "center" }}>
                <button type="button" className="po-btn" onClick={load}>Yeniden dene</button>
                <button type="button" className="po-btn primary" onClick={onClose}>Listeye dön</button>
              </div>
            </div>
          )}

          {shipment && (
            <>
              {/* İşlemler */}
              {shipment.status === S.Draft && (
                <div className="po-actrow">
                  {canUpdate && <button type="button" className="po-btn small primary" disabled={busy} onClick={() => onShip(shipment)}>{I.send} Sevk et</button>}
                  {canUpdate && <button type="button" className="po-btn small" disabled={busy} onClick={() => onEdit(shipment)}>{I.edit} Düzenle</button>}
                  <button type="button" className="po-btn small" disabled={busy} onClick={() => onPrint(shipment)}>{I.print} Yazdır</button>
                  {canUpdate && <button type="button" className="po-btn small danger push" disabled={busy} onClick={() => onCancel(shipment)}>İptal et</button>}
                  {canDelete && <button type="button" className={`po-btn small danger ${canUpdate ? "" : "push"}`} disabled={busy} onClick={() => onDelete(shipment)}>{I.trash} Sil</button>}
                </div>
              )}
              {shipment.status === S.Shipped && (
                <div className="po-actrow">
                  {canUpdate && <button type="button" className="po-btn small primary" disabled={busy} onClick={() => onDeliver(shipment)}>{I.receive} Teslim edildi</button>}
                  <button type="button" className="po-btn small" disabled={busy} onClick={() => onPrint(shipment)}>{I.print} Yazdır</button>
                  {canUpdate && <button type="button" className="po-btn small danger push" disabled={busy} onClick={() => onCancel(shipment)}>İptal et</button>}
                </div>
              )}
              {shipment.status === S.Delivered && (
                <div className="po-actrow">
                  <button type="button" className="po-btn small" disabled={busy} onClick={() => onPrint(shipment)}>{I.print} Yazdır</button>
                  <span className="po-hint push">Teslim edilmiş sevkiyat iptal edilemez.</span>
                </div>
              )}
              {shipment.status === S.Cancelled && (
                <>
                  <div className="po-note"><b>İptal edildi{shipment.cancelledAt ? ` · ${shFmtDate(shipment.cancelledAt)}` : ""}</b>{shipment.cancelReason ? ` — ${shipment.cancelReason}` : ""}</div>
                  <div className="po-actrow">
                    <button type="button" className="po-btn small" disabled={busy} onClick={() => onPrint(shipment)}>{I.print} Yazdır</button>
                  </div>
                </>
              )}

              {/* Rakamlar */}
              <div className="po-figs">
                <div className="po-fig"><span className="l">Toplam miktar</span><span className="v">{unitTotals || "—"}</span><span className="s">{lines.length} sipariş satırı</span></div>
                <div className="po-fig"><span className="l">Koli / ağırlık</span><span className="v">{pk != null ? `${pk} koli` : "—"}</span><span className="s">{shipment.grossWeightKg != null ? `${shFmtQty(shipment.grossWeightKg)} kg` : "ağırlık girilmedi"}</span></div>
                {shipment.status === S.Draft && <div className="po-fig"><span className="l">Sevk tarihi</span><span className="v">{shFmtDate(shipment.shipmentDate)}</span><span className="s">henüz sevk edilmedi</span></div>}
                {shipment.status === S.Shipped && <div className={`po-fig ${age.state}`}><span className="l">Yolda</span><span className="v">{age.days ?? 0} gün</span><span className="s">sevk {shFmtDateTime(shipment.shippedAt)}</span></div>}
                {shipment.status === S.Delivered && <div className="po-fig"><span className="l">Teslim</span><span className="v">{shFmtDateTime(shipment.deliveredAt)}</span><span className="s">{shipment.receivedBy || "—"}</span></div>}
                {shipment.status === S.Cancelled && <div className="po-fig late"><span className="l">İptal</span><span className="v">{shFmtDate(shipment.cancelledAt)}</span><span className="s">{anyStock ? "stok çıkışları geri alındı" : "stok hareketi yoktu"}</span></div>}
              </div>

              {/* Künye */}
              <dl className="po-facts">
                <div className="wide"><dt>Teslimat adresi</dt>
                  <dd>{shipment.deliveryTitle || shipment.firmName}
                    <small>{[shipment.deliveryAddress, [shipment.deliveryDistrict, shipment.deliveryCity].filter(Boolean).join(" / ")].filter(Boolean).join(" · ") || "Adres girilmedi"}</small>
                    <small>{shipment.deliveryContact}{shipment.deliveryContact && shipment.deliveryPhone ? " · " : ""}{shipment.deliveryPhone && <a href={`tel:${shipment.deliveryPhone}`}>{shipment.deliveryPhone}</a>}{!shipment.deliveryContact && !shipment.deliveryPhone && "İletişim bilgisi yok"}</small>
                  </dd></div>
                <div><dt>Nakliyeci / kargo</dt><dd>{shipment.carrier || "—"}{shipment.vehiclePlate && <small><span className="po-codepill sh-plate">{shipment.vehiclePlate}</span></small>}</dd></div>
                <div><dt>Şoför</dt><dd>{shipment.driverName || "—"}{shipment.driverTckn && <small>TCKN {shipment.driverTckn}</small>}</dd></div>
                <div><dt>Takip no</dt><dd>{shipment.trackingNo || "—"}</dd></div>
                <div><dt>Resmi irsaliye</dt><dd>{shipment.waybillNo || "—"}{shipment.waybillDate && <small>{shFmtDate(shipment.waybillDate)}</small>}</dd></div>
                <div><dt>Oluşturan</dt><dd>{shipment.createdBy || "—"}<small>{shFmtDateTime(shipment.createdAt)}</small></dd></div>
                {shipment.updatedAt && <div><dt>Güncelleyen</dt><dd>{shipment.updatedBy || "—"}<small>{shFmtDateTime(shipment.updatedAt)}</small></dd></div>}
                <div className="wide"><dt>Not</dt><dd>{shipment.note || "—"}</dd></div>
              </dl>

              {/* Siparişler */}
              <section className="po-sec">
                <h3>Siparişler <em>{lines.length}</em></h3>
                {lines.length === 0 ? (
                  <div className="po-blank small"><b>Bu sevkiyatta satır yok</b></div>
                ) : (
                  <>
                    <div className="po-table-wrap tight">
                      <table className="po-table small">
                        <thead><tr><th>#</th><th>Sipariş</th><th className="po-hide-sm">Sipariş tarihi</th><th className="num">Sevk miktarı</th><th className="num">Koli</th><th className="num">Sipariş toplamı</th>{showStock && <th>Stok</th>}</tr></thead>
                        <tbody>
                          {lines.map((l, i) => {
                            const rem = l.orderQuantity - l.orderShipped;
                            return (
                              <tr key={l.shipmentLineId} className="static">
                                <td>{i + 1}</td>
                                <td><button type="button" className="po-link" onClick={() => navigate(`/orders/detail/${l.orderId}`)}>{l.modelCode || "Model yok"}</button>{l.modelName && <div className="po-sub">{l.modelName}</div>}</td>
                                <td className="po-hide-sm">{shFmtDate(l.orderDate)}</td>
                                <td className="num"><span className="po-amt">{shFmtQty(l.quantity)} {l.unit}</span></td>
                                <td className="num">{l.packageCount ?? "—"}</td>
                                <td className="num">{shFmtQty(l.orderShipped)} / {shFmtQty(l.orderQuantity)}
                                  {rem > EPS ? <div className="po-sub">kalan {shFmtQty(rem)}</div> : <div><span className="po-badge ok">Tam</span></div>}
                                  {shipment.status === S.Draft && <div className="po-sub">bu taslak dahil</div>}
                                </td>
                                {showStock && (
                                  <td>{!l.hasStockMovement ? <span className="po-badge muted">hareket yok</span>
                                    : shipment.status === S.Cancelled ? <span className="po-badge warn">Geri alındı</span>
                                    : <span className="po-badge ok">Düşüldü</span>}</td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="po-totals">
                      <div className="po-total-row"><span>Sipariş</span><b>{lines.length}</b></div>
                      <div className="po-total-row"><span>Koli</span><b>{linePk || "—"}</b></div>
                      <div className="po-total-row grand"><span>Toplam</span><b>{unitTotals || "—"}</b></div>
                    </div>
                  </>
                )}
              </section>

              {/* Geçmiş */}
              <section className="po-sec">
                <h3>Geçmiş</h3>
                <div className="po-timeline">
                  <div className="po-tl"><b>Oluşturuldu</b><small>{shFmtDateTime(shipment.createdAt)} · {shipment.createdBy || "—"}</small></div>
                  {shipment.shippedAt && (
                    <div className="po-tl">
                      <b>Sevk edildi</b>
                      <small>{shFmtDateTime(shipment.shippedAt)} · {[shipment.carrier, shipment.vehiclePlate].filter(Boolean).join(" · ") || "—"}</small>
                      {stockOn && (
                        <ul className="po-tl-lines">
                          {lines.map((l) => <li key={l.shipmentLineId}>{l.modelCode || "Model yok"} — {shFmtQty(l.quantity)} {l.unit}{!l.hasStockMovement && <span className="po-badge muted">stok hareketi yok</span>}</li>)}
                        </ul>
                      )}
                    </div>
                  )}
                  {shipment.status === S.Shipped && <div className="po-tl"><b>Yolda</b><small>{age.text} · teslim onayı bekleniyor</small></div>}
                  {shipment.deliveredAt && <div className="po-tl"><b>Teslim edildi</b><small>{shFmtDateTime(shipment.deliveredAt)} · {shipment.receivedBy || "—"}</small></div>}
                  {shipment.status === S.Cancelled && <div className="po-tl"><b>İptal edildi</b><small>{shFmtDateTime(shipment.cancelledAt)}{shipment.cancelReason ? ` — ${shipment.cancelReason}` : ""}{anyStock ? " · stok çıkışları geri alındı" : ""}</small></div>}
                </div>
                {shipment.status === S.Draft && <p className="po-hint">Henüz sevk edilmedi.</p>}
              </section>

              {hasModule("receivables") && has("receivables:view") && (
                <div className="po-actrow">
                  <button type="button" className="po-link" onClick={() => navigate(`/receivables/firm/${shipment.firmId}`)}>Firma cari hesabı</button>
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
};

export default ShipmentDrawer;
